.button {
	--button-text: var(--color-text);
	--button-padding: var(--size-3) var(--size-5);
	--button-highlight-offset: 1px;
	@container style(--theme: dark) {
		--button-highlight-offset: -1px;
	}

	/* base */
	--button-bg: light-dark(var(--gray-200), var(--gray-700));
	--button-shine: light-dark(var(--gray-100), rgb(from var(--gray-600) r g b / 35%));
	--button-border: light-dark(var(--color-border), transparent);
	--button-shadow:
		inset 0 0 8px light-dark(transparent, rgb(from var(--gray-600) r g b / 10%)), var(--shadow-sm);
	/* hover */
	--button-bg-hover: light-dark(var(--gray-200), rgb(from var(--gray-600) r g b / 75%));
	--button-shine-hover: light-dark(var(--gray-100), rgb(from var(--gray-500) r g b / 18%));
	--button-border-hover: light-dark(var(--color-border-hover), transparent);
	--button-shadow-hover:
		inset 0 0 8px light-dark(transparent, rgb(from var(--gray-100) r g b / 4%)), var(--shadow-sm);
	/* active */
	--button-bg-active: light-dark(var(--gray-200), rgb(from var(--gray-600) r g b / 30%));
	--button-shine-active: light-dark(
		rgb(from var(--gray-300) r g b / 70%),
		rgb(from var(--gray-900) r g b / 30%)
	);
	--button-bgimage: radial-gradient(
		ellipse 175% 100% at bottom center,
		transparent 65%,
		var(--button-shine) 100%
	);
	--button-border-active: light-dark(
		var(--color-border-active),
		rgb(from var(--gray-900) r g b / 15%)
	);
	--button-shadow-active: inset 0 0 8px
		light-dark(transparent, rgb(from var(--gray-100) r g b / 6%));

	/* kinds */

	&[data-kind="primary"] {
		--button-text: light-dark(var(--blue-400), var(--blue-300));
	}

	&[data-kind="positive"] {
		--button-text: light-dark(var(--color-success-foreground), var(--green-500));
	}

	&[data-kind="negative"] {
		--button-text: light-dark(var(--color-error-foreground), var(--red-500));
	}

	&[data-kind="warning"] {
		--button-text: var(--color-warning-foreground);
	}

	/* levels */

	&[data-level="primary"] {
		--button-text: var(--gray-100);
		--button-bg: var(--color-primary);
		--button-bg-hover: color-mix(in srgb, var(--color-primary), var(--color-primary-hover) 28%);
		--button-bg-active: var(--color-primary);
		--button-shine: rgb(from var(--blue-300) r g b / 50%);
		--button-shine-hover: rgb(from var(--blue-200) r g b / 35%);
		--button-shine-active: rgb(from var(--blue-500) r g b / 40%);

		&[data-kind="positive"] {
			--button-bg: var(--green-700);
			--button-bg-hover: var(--green-500);
			--button-bg-active: var(--green-700);
			--button-shine: rgb(from var(--green-400) r g b / 50%);
			--button-shine-hover: rgb(from var(--green-400) r g b / 100%);
			--button-shine-active: rgb(from var(--green-800) r g b / 12%);
		}

		&[data-kind="negative"] {
			--button-bg: var(--red-600);
			--button-bg-hover: var(--red-500);
			--button-bg-active: var(--red-600);
			--button-shine: rgb(from var(--red-400) r g b / 50%);
			--button-shine-hover: rgb(from var(--red-400) r g b / 75%);
			--button-shine-active: rgb(from var(--red-700) r g b / 12%);
		}
	}

	&[data-level="tertiary"] {
		--button-border: transparent;
		--button-bg: transparent;
		--button-bg-hover: var(--color-surface-hover);
		--button-bg-active: rgb(from var(--color-surface-hover) r g b / 70%);
		--button-shine: none;
		--button-shine-hover: none;
		--button-shine-active: light-dark(
			rgb(from var(--gray-300) r g b / 70%),
			rgb(from var(--gray-900) r g b / 30%)
		);
		--button-shadow: none;
	}

	/* sizes */

	&[data-size="small"] {
		font-size: var(--text-caption);
		--button-padding: var(--size-2) var(--size-3);
	}

	&[data-size="large"] {
		--button-padding: var(--size-4) var(--size-7);
	}

	display: inline-grid;
	justify-items: center;
	align-items: center;
	min-height: 1.75rem;
	gap: var(--size-4);
	border-radius: var(--radius-lg);
	font-size: var(--text-body);
	font-weight: 500;
	flex-shrink: 0;
	padding: var(--button-padding);
	background-image: var(--button-bgimage);
	background-clip: padding-box;
	background-color: var(--button-bg);
	border: var(--size-border) solid var(--button-border);
	box-shadow: var(--button-shadow);
	color: var(--button-text);
	text-decoration: none;
	white-space: nowrap;
	transition:
		opacity 0.1s ease,
		background-color 0.1s ease,
		background-image 0.1s ease,
		border-color 0.1s ease,
		box-shadow 0.1s ease;

	&:disabled {
		opacity: 0.5;
	}

	&:not(:disabled):hover,
	&[data-hover="true"] {
		--button-shine: var(--button-shine-hover);
		background-color: var(--button-bg-hover);
		border-color: var(--button-border-hover);
		box-shadow: var(--button-shadow-hover);
	}

	&:not(:disabled):active,
	&[aria-pressed="true"] {
		--button-shine: var(--button-shine-active);
		background-color: var(--button-bg-active);
		border-color: var(--button-border-active);
		box-shadow: var(--button-shadow-active);
	}

	&:focus-visible {
		--button-border: var(--button-border-hover);
		--button-bg: var(--button-bg-hover);
		--button-shine: var(--button-shine-hover);
	}
}
